/* =========================================================
   __                 ____  _
  |   \  ___  __ __  |  _ || |  _ _   ___
  | |) |/ -_) \ V /  |  __|| | | | | / -_)
  |___/ \___|  \_/   |_|   |_| \_,_| \___| o o o

========================================================= */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Avatar from "@mui/material/Avatar";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import TableHeader from "components/Table/TableHeader";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Navbar from "examples/Navbars/NavBar";
import Footer from "examples/Footer";
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";

const initAttr = {
  code: "",
  filepic: "",
  lineid: "",
  firstname: "",
  lastname: "",
  tel: "",
  email: "",
  idline: "",
  enable: "Y",
  date_create: "",
  date_update: "",
};

const initFind = {
  search: "",
  order_by: "tb.id",
  order_sort: "desc",
  page: 1,
  perPage: 10,
  pageTotal: 0,
  status: "ALL",
};

function SaleOrder() {
  const mod = "saleorder";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: "tb.filepic", label: t(`attr.image`), align: "center" },
    { id: "tb.id", label: t(`attr.id`), align: "center" },
    { id: "tb.member_code", label: "สมาชิก", align: "left" },
    { id: "tb.amount", label: "จำนวนเงิน", align: "right" },
    { id: "tb.vat", label: "Vat", align: "right" },
    { id: "tb.total", label: "รวม", align: "right" },
    { id: "tb.discount", label: "ส่วนลด", align: "right" },
    { id: "tb.nettotal", label: "รวมสุทธิ", align: "right" },
    { id: "tb.status", label: t(`attr.enable`), align: "center" },
    { id: "tb.date_create", label: t(`attr.date_create`), align: "center" },
    { id: "edit", label: "", align: "center", sort: false },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon="notifications"
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData("POST", `/app/req/${mod}-view`, app.token, {
      lang: app.lang,
      token: app.token,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      sort: data.order_sort,
      status: "ALL",
    }).then((result) => {
      if (result.status === "OK") {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const approve = (item) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData("POST", `/app/req/${mod}-approve`, app.token, {
          token: app.token,
          lang: app.lang,
          code: item.code,
        }).then((result) => {
          setLoading(false);

          if (result.status === "OK") {
            view(find);
            showNotify(t(`alert.editcomplete`), "success");
          } else {
            showNotify(t(`alert.saveerror`), "error");
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? "desc" : "asc" });
  };

  const init = () => {
    FetchData("POST", `/app/req/${mod}-init`, app.token, {
      token: app.token,
      lang: app.lang,
    }).then((result) => {
      if (result.status === "OK") {
        setAttr(initAttr);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.name} src={item.member_pic} variant="square" />;
  };

  const btnEdit = (item) => {
    if (item.status < 3)
      return (
        <MDButton variant="text" color="success" size="small" onClick={() => approve(item)}>
          <Icon>border_color</Icon> Approve
        </MDButton>
      );

    return null;
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <form
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    view({ ...find, page: 1 });
                  }}
                >
                  <MDInput
                    label={t(`element.search`)}
                    onChange={(e) => setFind({ ...find, search: e.target.value })}
                    value={find.search}
                    inputProps={{
                      form: {
                        autocomplete: "off",
                      },
                    }}
                  />
                </form>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center">{imageDisplay(row)}</TableCell>
                      <TableCell align="center">{row.id}</TableCell>
                      <TableCell align="left">{row.member_name}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.amount)}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.vat)}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.total)}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.discount)}</TableCell>
                      <TableCell align="right">{Func.numberDisplay(row.nettotal)}</TableCell>
                      <TableCell align="center">{row.status_name}</TableCell>
                      <TableCell align="center">{Func.dateTimeDisplay(row.date_create)}</TableCell>
                      <TableCell align="center">{btnEdit(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDButton variant={show ? "contained" : "outlined"} color="dark" onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant="outlined"
                  color="secondary"
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label="ชื่อ"
            onChange={(e) => setAttr({ ...attr, firstname: e.target.value })}
            value={attr.firstname}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="นามสกุล"
            onChange={(e) => setAttr({ ...attr, lastname: e.target.value })}
            value={attr.lastname}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="เบอร์โทร"
            onChange={(e) => setAttr({ ...attr, tel: e.target.value })}
            value={attr.tel}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="อีเมล"
            onChange={(e) => setAttr({ ...attr, email: e.target.value })}
            value={attr.email}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="Line ID"
            onChange={(e) => setAttr({ ...attr, idline: e.target.value })}
            value={attr.idline}
            variant="standard"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={attr.enable === "Y"}
                onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? "Y" : "N" })}
                inputProps={{
                  "aria-label": "enable",
                }}
              />
            }
            label="Enable"
          />
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.setting`), t(`menu.saleorder`)]} />
      {renderList()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default SaleOrder;
